import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getFirebaseErrorMessage } from '../utils/firebaseErrors';
import { trackUserLogin, identifyUser } from '../services/analytics';
import { getPendingDealDoc, PENDING_DEAL_EVENT } from '../services/branch';
import { Deal } from '../types/deal';
import CoBrandHeader from './CoBrandHeader';
import CalloutCard from './CalloutCard';
import LanguageSwitcher from './LanguageSwitcher';
import { updateEmailSubscriptionPreference } from '../services/dealRepository';
import { useFirebaseImageUrl } from '../hooks/useFirebaseImageUrl';
import { getPageImage, getPageLayout, layoutToStyle } from '../utils/dealImages';
import { useAwaitingDeal } from '../hooks/useAwaitingDeal';
import { t } from '../i18n';
import './Auth.css';
import './Promo.css';


const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingDeal, setPendingDeal] = useState<Deal | null>(null);
  const { login, loginWithGoogle, loginWithApple, currentUser } = useAuth();
  const navigate = useNavigate();
  const productImage = useFirebaseImageUrl(getPageImage(pendingDeal, 'login'));
  const [heroLoaded, setHeroLoaded] = useState(false);
  const awaitingDeal = useAwaitingDeal(productImage, heroLoaded);

  useEffect(() => {
    if (currentUser) {
      navigate(currentUser.emailVerified ? '/offers' : '/verify-email');
    }
  }, [currentUser, navigate]);

  useEffect(() => {
    // Pick up the pending deal from Branch — now, and again if it arrives async
    const sync = () => {
      const deal = getPendingDealDoc();
      if (deal) {
        setPendingDeal(deal);
        console.log('[Login] Showing pending deal banner:', deal.name);
      }
    };
    sync();
    window.addEventListener(PENDING_DEAL_EVENT, sync);
    return () => window.removeEventListener(PENDING_DEAL_EVENT, sync);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Store the email and password before any async operations
    const userEmail = email;
    const userPassword = password;

    try {
      setError('');
      setLoading(true);
      const userCredential = await login(userEmail, userPassword);

      // Track user login
      if (userCredential.user) {
        identifyUser(userCredential.user.uid, { email: userCredential.user.email || userEmail });
        trackUserLogin(userCredential.user.email || userEmail, userCredential.user.uid);

        // Update email subscription preference if checked
        if (marketingOptIn) {
          await updateEmailSubscriptionPreference(userCredential.user.uid, true);
        }
      }

      // Unverified users stay logged in and land on the verify-email page
      navigate(userCredential.user?.emailVerified ? '/offers' : '/verify-email');
    } catch (err: any) {
      setError(getFirebaseErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      setError('');
      setLoading(true);
      const userCredential = await loginWithGoogle();

      // Track user login
      if (userCredential.user) {
        identifyUser(userCredential.user.uid, { email: userCredential.user.email || '' });
        trackUserLogin(userCredential.user.email || '', userCredential.user.uid);

        // Update email subscription preference if checked
        if (marketingOptIn) {
          await updateEmailSubscriptionPreference(userCredential.user.uid, true);
        }
      }

      navigate(userCredential.user?.emailVerified ? '/offers' : '/verify-email');
    } catch (err: any) {
      setError(getFirebaseErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleAppleLogin = async () => {
    try {
      setError('');
      setLoading(true);
      const userCredential = await loginWithApple();

      // Track user login
      if (userCredential.user) {
        identifyUser(userCredential.user.uid, { email: userCredential.user.email || '' });
        trackUserLogin(userCredential.user.email || '', userCredential.user.uid);

        // Update email subscription preference if checked
        if (marketingOptIn) {
          await updateEmailSubscriptionPreference(userCredential.user.uid, true);
        }
      }

      navigate(userCredential.user?.emailVerified ? '/offers' : '/verify-email');
    } catch (err: any) {
      setError(getFirebaseErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="hp-page">
      <LanguageSwitcher />
      <div className="hp-split">
        <div className={`hp-hero${awaitingDeal ? ' hp-hero--awaiting' : ''}`}>
          {productImage && (
            <img src={productImage} alt={pendingDeal?.name || ''} className="hp-hero-fill" style={layoutToStyle(getPageLayout(pendingDeal, 'login'))} onLoad={() => setHeroLoaded(true)} />
          )}
        </div>

        <div className="hp-panel">
          <div className="hp-panel-inner">
            <CoBrandHeader deal={pendingDeal} />

            <CalloutCard deal={pendingDeal} page="login" />

            <div className="hp-heading">
              <h1 className="hp-headline">{t('login.heading')}</h1>
              <p className="hp-subtitle">
                {t('promo.loginSubtitle')}
              </p>
            </div>

            {error && <div className="error-message">{error}</div>}

            <form onSubmit={handleSubmit} className="auth-form">
          <div className="input-field">
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder={t('common.email')}
            />
          </div>

          <div className="input-field">
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder={t('common.password')}
            />
          </div>

          <div className="marketing-opt-in">
            <label className="checkbox-container">
              <input
                type="checkbox"
                checked={marketingOptIn}
                onChange={(e) => setMarketingOptIn(e.target.checked)}
              />
              <span className="checkmark"></span>
              <span className="checkbox-label">
                {t('login.marketingOptIn')}
              </span>
            </label>
          </div>

          <button type="submit" disabled={loading} className="hp-cta">
            {loading ? t('login.loggingIn') : t('common.continue')}
          </button>
        </form>

        <div className="divider">
          <span>{t('login.divider')}</span>
        </div>

        <div className="social-buttons">
          <button onClick={handleGoogleLogin} disabled={loading} className="btn-social">
            <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            {t('login.googleButton')}
          </button>

          <button onClick={handleAppleLogin} disabled={loading} className="btn-social">
            <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.08l.15-.1zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" fill="currentColor"/>
            </svg>
            {t('login.appleButton')}
          </button>
        </div>

            <p className="hp-secondary-link">
              {t('login.noAccount')} <Link to="/signup">{t('login.signUpLink')}</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
